import React, { useState, useEffect, useCallback, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams, Link } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { License, Office, Agent, Address, Product, PaymentMethod, PaymentGateway, StatusHistoryItem, PaymentInfoItem, Supplier } from "types";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import PaymentFormManualOrder from "../../components/PaymentFormManualOrder";
import { getAuthCookie } from "../../utils/authCookies";

type ProductObj = {
    product_id: string;
    agent_id: string;
    office_id: string;
    inventory_type: string;
    product: Product;
    created_by: {
        id: string;
        name: string;
    };
};

type Selected = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const mapContainerStyle = {
    width: "100%",
    height: "400px",
};

const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

const BrokerageTeammemberEditInstallation = () => {
    const { installationId } = useParams<{ installationId: string }>();
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const [teamMembers, setTeamMembers] = useState<any[]>([]);
    const [selectedTeamMember, setSelectedTeamMember] = useState<any | null>(null);
    const [offices, setOffices] = useState<Office[]>([]);
    const [officeAgents, setOfficeAgents] = useState<Agent[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([])
    const [preview, setPreview] = useState<string | null>(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<any[]>([]);
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [productObjs, setProductObjs] = useState<ProductObj[]>([]);
    const [selectedProductOption, setSelectedProductOption] = useState<any | null>(null);
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
    const [installationCost, setInstallationCost] = useState<number | null>(null);
    const [totalUnits, setTotalUnits] = useState<number>(0);
    const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
    const [isMarkerDraggable, setIsMarkerDraggable] = useState(false);
    const [localLawsChecked, setLocalLawsChecked] = useState(false);
    const [installationOrder, setInstallationOrder] = useState<any>();
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
    const [payment, setPayment] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");

    const navigate = useNavigate();

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: apiKey,
        libraries,
    });

    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);

    const [selectedOffice, setSelectedOffice] = useState<Selected | null>(null);

    const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<Selected | null>(null);

    const [selectedAgent, setSelectedAgent] = useState<Selected | null>(null);
    const [selectedInstaller, setSelectedInstaller] = useState<{
        value: string;
        label: string;
    } | null>(null);

    const [selectedBillingAddress, setSelectedBillingAddress] = useState<Address | null>(null);

    const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] = useState<Address | null>(null);

    const [installationAddress, setInstallationAddress] = useState<Address>();

    const buyerOptions: { value: string; label: string }[] = [
        { value: "office", label: "Office" },
        { value: "agent", label: "Agent" },
    ];

    // const teamMemberOptions = teamMembers?.map((member) => ({
    //     value: member.id,
    //     label: `${member.firstName} ${member.lastName}`,
    //     email: member.email,
    // }));

    const officeOptions = offices?.map((office) => ({
        value: office?._id,
        label: office?.officeName,
        firstName: office?.officeName,
        lastName: office?.officeName,
        email: "",
        phone: office?.officeAddress?.[0]?.phone,
    }));

    const officeAgentOptions = officeAgents?.map((agent) => ({
        value: agent.id,
        label: agent.firstName + " " + agent.lastName,
        firstName: agent.firstName,
        lastName: agent.lastName,
        email: agent.email,
        phone: agent.phone1,
    }));

    const agentOptions = agents?.map((agent) => ({
        value: agent.id,
        label: agent.firstName + " " + agent.lastName,
        firstName: agent.firstName,
        lastName: agent.lastName,
        email: agent.email,
        phone: agent.phone1,
    }));

    const productOptions = productObjs?.map((item) => ({
        value: item.product_id,
        label: item.product.name,
    }));

    const installerOptions = suppliers?.map((supplier) => ({
        value: supplier.supplier_id,
        label: supplier.name,
    }));

    const priorityOptions = [
        { value: 1, label: "High" },
        { value: 2, label: "Medium" },
        { value: 3, label: "Low" },
    ];

    useEffect(() => {
        // console.log("Installation ID:", installationId);
        const fetchOrderDetails = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/${installationId}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await response.json();
                console.log("Installation Orders:", data);
                if (!response.ok) throw new Error(data.message || "Failed to fetch order details");

                setInstallationOrder(data);
            } catch (err: any) {
                console.error("Order Detail Fetch Error:", err.message);
            }
        };

        fetchOrderDetails();
    }, [installationId, token]);

    useEffect(() => {
        if (installationOrder) {
            const addr = installationOrder?.installation_details?.installation_address;
            setInstallationAddress(addr);
        }
        if (installationOrder?.inventory_source === "office") {
            fetchOffices();
        }
    }, [installationOrder]);

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    // const featchBrokerageTeammembers = async () => {

    //     try {
    //         const response = await fetch(
    //             `${process.env.REACT_APP_API_URL}/users-search?created_by.id=${userId}&licenseType=BROKERAGE_TEAM_MEMBER`,
    //             {
    //                 method: "GET",
    //                 headers: {
    //                     Authorization: `Bearer ${token}`,
    //                     "Content-Type": "application/json",
    //                 },
    //             }
    //         );
    //         const data = await response.json();
    //         console.log("featchBrokerageTeammembers :", data);

    //         if (!response.ok) {
    //             toast.error(data.error || "Failed to fetch offices");
    //         } else {
    //             setTeamMembers(data);
    //             const normalizedOffices = data.flatMap((member: any) =>
    //                 (member.offices || []).map((office: any) => ({
    //                     _id: office._id,
    //                     officeName: office.officeName,
    //                     officeType: office.officeType,
    //                     officeAddress: [office.officeAddress], // ✅ wrap in array to match expected type
    //                 }))
    //             );

    //             setOffices(normalizedOffices);
    //             // ✅ Set selectedOffice based on installationOrder
    //             if (installationOrder?.office?._id && normalizedOffices.length > 0) {
    //                 const matchedOffice = normalizedOffices.find(
    //                     (office: any) => office._id === installationOrder.office._id
    //                 );
    //                 if (matchedOffice) {
    //                     const officeOption = {
    //                         value: matchedOffice._id,
    //                         label: matchedOffice.officeName,
    //                         firstName: matchedOffice.officeName,
    //                         lastName: matchedOffice.officeName,
    //                         email: "",
    //                         phone: matchedOffice.officeAddress?.[0]?.phone || "",
    //                     };
    //                     setSelectedOffice(officeOption);
    //                     fetchLinkedProducts("office", officeOption.value, installationOrder?.signage_id);
    //                     fetchOfficeAgents(officeOption);
    //                 }
    //             }
    //         }
    //     } catch (err) {
    //         setError(
    //             err instanceof Error ? err.message : "An unknown error occurred"
    //         );
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const fetchOffices = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/users/${userId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch license data");
            }

            const data = await response.json();
            console.log("Fetched License Data:", data);

            if (data) {
                setOffices(data?.offices);
                // Find default office by _id matching installationOrder.office._id
                const matchedOffice = data.offices.find(
                    (office: any) => office._id === installationOrder?.office?._id
                );

                if (matchedOffice) {
                    const officeOption = {
                        value: matchedOffice._id,
                        label: matchedOffice.officeName,
                        firstName: matchedOffice.officeName, // or use real firstName if available
                        lastName: matchedOffice.officeName,  // or use real lastName if available
                        email: "", // fill if you have data
                        phone: matchedOffice.officeAddress?.phone || "",
                    };
                    setSelectedOffice(officeOption);
                    fetchOfficeAgents(officeOption);
                }

            } else {
                console.error("Incomplete data", data);
            }
        } catch (error) {
            console.error("Error fetching license data:", error);
            setError("Failed to fetch license data.");
        } finally {
            setLoading(false);
        }
    };

    const fetchOfficeAgents = async (office: any) => {
        setSelectedOffice(office);
        console.log("selectedOffices In fetchOfficeAgents:", office);
        let officeId = office?.value;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/agent/office/${officeId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = await response.json();
            console.log("officeAgents :", data);

            if (!response.ok) {
                toast.error(data.error || "Failed to fetch agents");
            } else {
                setOfficeAgents(data);
            }
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/users-search?userType=agent`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch agents");
                }

                const data: Agent[] = await response.json();
                console.log(data);
                setAgents(data);
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
            } finally {
                setLoading(false);
            }
        };

        fetchAgents();
    }, [token]);

    const fetchSuppliersByUserId = async (id: string) => {
        const newId = formik.values.userType === "office" ? userId : id
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/agent/suppliers/${newId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch supplier list");
            }

            const data = await response.json();
            console.log("Suppliers:", data);
            setSuppliers(data);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchUserAddresses = async (user: any) => {
        console.log("fetchUserAddresses IN")
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/user-addresses/${user?.value}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = await response.json();
            console.log("user addresses :", data);
            // setAddresses(data.addresses);
            setAddressObjects(data);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchOfficeAddresses = async (office: any) => {
        console.log("fetchOfficeAddresses IN")
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/offices/${office?.value}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = await response.json();
            console.log("office addresses :", data.officeAddress);
            setAddressObjects(data.officeAddress);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedOfficeAgent) {
            fetchUserAddresses(selectedOfficeAgent);
        } else if (selectedAgent) {
            fetchUserAddresses(selectedAgent);
        } else if (selectedOffice) {
            fetchOfficeAddresses(selectedOffice);
        }
    }, [selectedAgent, selectedOfficeAgent, selectedOffice, token]);

    const addressOptions =
        addressObjects !== null
            ? addressObjects?.map((address) => ({
                ...address,
                value: address._id,
                label: address.label,
            }))
            : [];

    const fetchLinkedProducts = async (
        userType: "agent" | "office",
        userId: string,
        signageCompanyId: string
    ) => {
        console.log("fetchLinkedProducts IN");
        if (!userId || !signageCompanyId) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/inventory/${userType}/${userId}/signage/${signageCompanyId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await response.json();
            console.log("Linked Products:", data);
            setProductObjs(data);

            if (!response.ok) {
                toast.error(data?.error || "Failed to fetch linked products");
            } else {
                // setLinkedProducts(data); if needed
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Unknown error occurred");
        }
    };

    // based on instaletion Address we are calling the dyniamc cost
    useEffect(() => {
        const fetchInstallationCost = async () => {
            if (
                !installationAddress?.latitude ||
                !installationAddress?.longitude ||
                installationAddress.latitude === 0 ||
                installationAddress.longitude === 0
            ) {
                toast.warning(
                    "Selected installation address is missing latitude/longitude. Please enter the address manually."
                );
                return;
            }

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/dynamic_cost/${selectedInstaller?.value}`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            installation_address: {
                                addressLine1: installationAddress.addressLine1,
                                addressLine2: installationAddress.addressLine2,
                                city: installationAddress.city,
                                state: installationAddress.state,
                                country: installationAddress.country,
                                zip: installationAddress.zip,
                                cityCode: installationAddress.cityCode,
                                stateCode: installationAddress.stateCode,
                                countryCode: installationAddress.countryCode,
                                latitude: installationAddress.latitude,
                                longitude: installationAddress.longitude,
                            }
                        }),
                    }
                );

                const data = await response.json();
                console.log("Installation cost:", data);
                if (!response.ok) throw new Error(data?.error || "Failed to fetch cost");

                setInstallationCost(data.cost || 0); // adjust based on response shape
            } catch (error: any) {
                console.error("Cost fetch error:", error.message);
                setInstallationCost(null);
            }
        };
        if (installationAddress) {
            fetchInstallationCost();
        }
    }, [installationAddress, selectedInstaller]);


    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            userType: installationOrder?.user_type,
            userFirstName: "",
            userLastName: "",
            userEmail: installationOrder?.user_email,
            userPhone: installationOrder?.user_phone,
            brokerageFirmsId: installationOrder?.brokerage_firm_id,
            teamMemberId: installationOrder?.team_member_id,
            company: "",
            installer: installationOrder?.installation_details.installer_id,
            office: installationOrder?.office,
            agent: installationOrder?.agent_id,
            scheduled_date: installationOrder?.installation_details?.scheduled_date?.split("T")[0] || "",
            jobs_today: 0,
            installeId: installationOrder?.installation_details.installer_id,
            priority: installationOrder?.priority,
            billing_address: installationOrder?.billing_address,
            installation_address: installationOrder?.installation_details.installation_address,
            isSameAsBilling: false,
            billingAddressId: installationOrder?.billing_address._id,
            installationAddressId: installationOrder?.installation_details.installation_address.label || "",
            location: "",
            products: "",
            instructions: "",
            payment_method: "",
            image: null,
            installation_charge: installationOrder?.installation_cost,
            discount: installationOrder?.discount_amount || 0,
            subtotal: installationOrder?.subtotal_amount,
            total: installationOrder?.total_amount,
            tax: 0,
            enable_partial_payment: false,
            partial_amount: 0,
            transactionId: "",
            paymentMethod: "",
        },
        validationSchema: Yup.object({
            // installer: Yup.string().required("Installer is required"),
            // installation_date: Yup.string().required("Installation date is required"),
            // billing_address: Yup.string().required("Billing address is required"),
            // installation_address: Yup.string().required(
            //     "Installation address is required"
            // ),
            // image: Yup.mixed().required("Image is required"),
        }),
        onSubmit: async (values) => {
            setFormSubmitting(true);
            const imageUrl = values.image ? await uploadFile(values.image) : null;
            let lineItems: any[] = [];

            // 📦 1. Regular products (agent or office)
            lineItems = selectedProducts.map((product) => {
                const matched = productObjs.find((p) => p.product_id === product.product_id);

                return {
                    product_id: product.product_id,
                    inventory_source: matched?.inventory_type || "", // agent or office
                    warehouse_id: null,
                    serial_ids: [],
                    product_name: matched?.product?.name || "",
                    sku: matched?.product?.sku || "",
                    quantity: Number(product.quantity),
                    unit_price: Number(matched?.product?.retail_price || 0),
                    total_price: Number(matched?.product?.retail_price || 0) * Number(product.quantity),
                    discount: 0,
                    tax_amount: 0,
                    inventory_deducted: false,
                    fulfillment_type: "stock",
                    production_status: product.productionStatus || "not started",
                    is_rotational: matched?.product?.is_rotational || false,
                    current_status: matched?.product?.current_status || "",
                    notes: product.notes || "",
                };
            });

            // 🔄 2. Rotational allocations
            // Object.entries(allocatedUnits).forEach(([linkedProductId, units]) => {
            //     if (units.length === 0) return;
            //     lineItems.push({
            //         product_id: units[0].rotationalProductId,
            //         inventory_source: "rotational",
            //         warehouse_id: units[0].warehouseId || "",
            //         serial_ids: units.map((u) => u.serial_id),
            //         product_name: "",
            //         sku: "",
            //         quantity: 1,
            //         unit_price: 0,
            //         total_price: 0,
            //         discount: 0,
            //         tax_amount: 0,
            //         inventory_deducted: false,
            //         fulfillment_type: "stock",
            //         production_status: null,
            //         is_rotational: true,
            //         current_status: "",
            //         notes: "",
            //     });
            // });
            try {
                // Example file upload handling

                const response = await fetch(`${process.env.REACT_APP_API_URL}/installation_orders/update/${installationId}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        // order_number: installationOrder?.order_number,
                        // inventory_source: inventorySource,
                        // source_id: sourceId,
                        // brokerage_firm_id: selectedBrokerageFirm?.value,
                        // brokerage_firm_name: selectedBrokerageFirm?.label,
                        // team_member_id: values.teamMemberId,
                        // office: officeInfo,
                        // agent_id: agentId,
                        // agent_name: agentName,
                        // signage_id: userId,
                        // signage_company_name: userName,
                        line_items: lineItems,
                        // customer_group_id: null,
                        // payment_method: values.payment_method,
                        installation_details: {
                            scheduled_date: new Date(values.scheduled_date).toISOString(),
                            installation_address: installationOrder?.installation_details?.installation_address,
                            installer_id: selectedInstaller?.value || null,
                            installer_name: null,
                            contact_number: null,
                            estimated_duration: null,
                            actual_installation_date: null,
                            installation_notes: null,
                            assignee_id: null,
                            tax_details: null,
                        },
                        billing_address: installationOrder?.billing_address,
                        additional_instructions: values.instructions,
                        // local_laws_compliance: true,
                        priority: values.priority,
                        uploaded_files: uploadedFileMeta || [],
                        subtotal_amount: values.subtotal,
                        installation_cost: parseFloat(String(installationCost)) || 0,
                        additional_cost: 0,
                        // tax_amount: 0,
                        discount_amount: null,
                        total_amount: values.total,
                        // overpaid_amount: 0,
                        version: installationOrder?.version,
                        // tax_inclusive: false,
                        // tax_details: null,
                    }),
                }
                );

                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.message || "Failed to create installation");
                }
                toast.success("Installation added successfully!");
                navigate("teammember/jobs/installations");
            } catch (error: any) {
                console.error(error);
                toast.error(`Error: ${error.message}`);
            } finally {
                setFormSubmitting(false);
                setUploadProgress(0);
            }
        },
    });

    const uploadFile = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "installation_orders");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200 || xhr.status === 201) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    reject(new Error("Failed to upload file"));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during file upload"));
            xhr.send(formData);
        });
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    const handlePlaceChanged = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            console.log("Place changed:", place);

            const addressComponents = place.address_components;
            console.log(addressComponents);

            if (addressComponents) {
                const getComponent = (type: string) => {
                    const component = addressComponents?.find(c => c.types.includes(type));
                    return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
                };

                const streetNumber = getComponent('street_number').long_name;
                const route = getComponent('route').long_name;

                const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
                const stateObj = getComponent('administrative_area_level_1');
                const postalCode = getComponent('postal_code').long_name;
                const countryObj = getComponent('country');


                const street = `${streetNumber} ${route}`.trim();

                const latitude = place.geometry?.location?.lat();
                const longitude = place.geometry?.location?.lng();

                console.log("Latitude:", latitude, "Longitude:", longitude);

                // Update form values
                // formik.setFieldValue('streetAddress1', street);
                // formik.setFieldValue('city', cityObj.long_name);
                // formik.setFieldValue('county', cityObj.long_name);
                // formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
                // formik.setFieldValue('state', stateObj.long_name);
                // formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
                // formik.setFieldValue('zipcode', postalCode);
                // formik.setFieldValue('country', countryObj.long_name);
                // formik.setFieldValue('countryCode', countryObj.short_name); 

                // if (latitude && longitude) {
                //   formik.setFieldValue('latitude', latitude);
                //   formik.setFieldValue('longitude', longitude);
                // }

                const installation_address = street + ',' + cityObj.long_name + ', ' + stateObj.long_name + ', ' + postalCode;
                setInstallationAddress({
                    _id: '',
                    label: installation_address,
                    addressLine1: street,
                    addressLine2: '',
                    country: countryObj.long_name,
                    county: cityObj.long_name,
                    city: cityObj.long_name,
                    state: stateObj.long_name,
                    zip: postalCode,
                    cityCode: cityObj.short_name,
                    stateCode: stateObj.short_name,
                    countryCode: countryObj.short_name,
                    latitude: latitude || 0,
                    longitude: longitude || 0,
                    phone: '',
                });

                formik.setFieldValue('installationAddressId', installation_address);
            }
        }
    };

    // seting the total units based on the selected products
    useEffect(() => {
        const newTotal = selectedProducts.reduce((sum, product) => sum + (product.quantity || 0), 0);
        setTotalUnits(newTotal);
    }, [selectedProducts]);

    // seting the installation address based on the selected billing address
    useEffect(() => {
        if (formik.values.isSameAsBilling) {
            const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
            const billing = isOffice ? selectedOfficeBillingAddress : selectedBillingAddress;

            if (billing) {
                console.log("Billing Address:", billing);
                setInstallationAddress(billing);
                formik.setFieldValue("installationAddressId", billing.label || "");
            }
        }
    }, [formik.values.isSameAsBilling, selectedBillingAddress, selectedOfficeBillingAddress]);

    // calculating the subtotal and total
    useEffect(() => {
        const subtotal = Number(installationCost) + Number(formik.values.tax);
        const total = subtotal - Number(formik.values.discount || 0);

        formik.setFieldValue("subtotal", subtotal);
        formik.setFieldValue("total", total);
    }, [installationCost, formik.values.tax, formik.values.discount]);

    // remove the preview images
    useEffect(() => {
        return () => {
            imagePreviews.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [imagePreviews]);

    useEffect(() => {
        if (
            installationAddress?.latitude &&
            installationAddress?.longitude &&
            installationAddress.latitude !== 0 &&
            installationAddress.longitude !== 0
        ) {
            console.log("Setting marker from installationAddress");
            setSelectedLocation({
                lat: installationAddress.latitude,
                lng: installationAddress.longitude,
            });
        }
    }, [installationAddress]);

    const handleMarkerDragEnd = async (event: google.maps.MapMouseEvent) => {
        const lat = event.latLng?.lat();
        const lng = event.latLng?.lng();

        if (!lat || !lng) return;

        setSelectedLocation({ lat, lng });

        try {
            const geocoder = new window.google.maps.Geocoder();
            const response = await geocoder.geocode({ location: { lat, lng } });

            if (response.results[0]) {
                const place = response.results[0];
                const addressComponents = place.address_components;

                const getComponent = (type: string) => {
                    const component = addressComponents.find((c: any) =>
                        c.types.includes(type)
                    );
                    return component
                        ? { long_name: component.long_name, short_name: component.short_name }
                        : { long_name: "", short_name: "" };
                };

                const streetNumber = getComponent("street_number").long_name;
                const route = getComponent("route").long_name;
                const cityObj =
                    getComponent("locality") ||
                    getComponent("administrative_area_level_2");
                const stateObj = getComponent("administrative_area_level_1");
                const postalCode = getComponent("postal_code").long_name;
                const countryObj = getComponent("country");

                const street = `${streetNumber} ${route}`.trim();

                const newAddress = {
                    _id: "",
                    label: place.formatted_address,
                    addressLine1: street,
                    addressLine2: "",
                    country: countryObj.long_name,
                    county: cityObj.long_name,
                    city: cityObj.long_name,
                    state: stateObj.long_name,
                    zip: postalCode,
                    cityCode: cityObj.short_name,
                    stateCode: stateObj.short_name,
                    countryCode: countryObj.short_name,
                    latitude: lat,
                    longitude: lng,
                    phone: "",
                };

                setInstallationAddress(newAddress);
                formik.setFieldValue("installationAddressId", place.formatted_address);
            }
        } catch (error) {
            console.error("Geocoder failed", error);
            toast.error("Unable to update address from marker position.");
        }
    };

    // geting the defult selectedAgent and installetion Addreess and getting the brokarageForms
    useEffect(() => {
        if (installationOrder?.agent_id && agents.length > 0) {
            const matchedAgent = agents.find(agent => agent.id === installationOrder.agent_id);
            if (matchedAgent) {
                const option = {
                    value: matchedAgent.id,
                    label: `${matchedAgent.firstName} ${matchedAgent.lastName}`,
                    firstName: matchedAgent.firstName,
                    lastName: matchedAgent.lastName,
                    email: matchedAgent.email,
                    phone: matchedAgent.phone1,
                };
                setSelectedAgent(option);
                fetchLinkedProducts("agent", option.value, installationOrder.signage_id);
                fetchSuppliersByUserId(option.value);
            }
        }

        if (installationOrder?.installation_details?.installation_address) {
            setInstallationAddress(installationOrder.installation_details.installation_address);
        }
        if (installationOrder?.team_member_id) {
            // featchBrokerageTeammembers();
            fetchSuppliersByUserId(installationOrder?.team_member_id);
        }
    }, [installationOrder, agents]);

    // set default suppliers to get the dynamic cost
    useEffect(() => {
        if (installationOrder && suppliers.length > 0) {
            const defaultSupplier = suppliers.find(
                (sup) => sup.supplier_id === installationOrder.signage_id
            );

            if (defaultSupplier) {
                const option = {
                    value: defaultSupplier.supplier_id,
                    label: defaultSupplier.name,
                };
                setSelectedInstaller(option);
                formik.setFieldValue("installer", option.value);
                // console.log("✅ Default Installer set:", option.value);
            } else {
                console.warn("⚠️ No matching supplier found for signage_id:", installationOrder.signage_id);
            }
        }
    }, [installationOrder, suppliers]);

    useEffect(() => {
        const fetchPaymentMethods = async () => {
            const id = installationOrder?.inventory_source === "office" ? installationOrder?.team_member_id : installationOrder?.agent_id
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/payment-methods/by-supplier/${userId}/by-user/${id}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                const data = await response.json();
                console.log("PaymentMethod :", data);
                setPaymentMethod(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching payment methods:", err);
            } finally {
                setLoading(false);
            }
        };

        if (userId && installationOrder?.inventory_source) {
            fetchPaymentMethods();
        }
    }, [token, userId, installationOrder?.inventory_source]);

    // setting defult selectedProducts
    useEffect(() => {
        if (installationOrder?.line_items?.length > 0) {
            const mapped = installationOrder.line_items.map((item: any) => ({
                product_id: item.product_id,
                inventory_type: item.inventory_source || "",
                name: item.product_name || "",
                sku: item.sku || "",
                product_location_status: item.current_status || "",
                user_id: "", // You can enrich this if needed based on agent/office ID from elsewhere
                quantity: item.quantity,
                updateStatus: "",
                productionStatus: item.production_status || "",
                is_rotational: item.is_rotational,
                allocatedCount: item.rotational_allocated || 0,
            }));

            setSelectedProducts(mapped);
            console.log("Mapped Products:", mapped);
        }
    }, [installationOrder]);

    const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);

    useEffect(() => {
        const fetchIntegrations = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${installationOrder?.signage_id}`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch integrations.");
                }
                const data = await response.json();
                console.log('Methods ', data);
                setIntegrations(data);
            } catch (err) {
                // toast.error("Failed to load integrations.");
                setError(err instanceof Error ? err.message : "An unknown error occurred");
            } finally {
                setLoading(false);
            }
        };

        fetchIntegrations();
    }, [token]);

    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);


    useEffect(() => {
        const stripeInfo = integrations?.find((method) => method.gateway === "stripe");
        console.log('Stripe info ', stripeInfo);
        if (stripeInfo && stripeInfo.publishable_key) {
            const promise = loadStripe(stripeInfo.publishable_key);
            setStripePromise(promise);
        }
    }, [integrations]);

    // setting defult billing Address
    useEffect(() => {
        if (!installationOrder || addressOptions.length === 0 || addressObjects.length === 0) return;

        const billingId = formik.values.billingAddressId;
        if (!billingId) return;

        const selectedBilling = addressOptions.find(address => address.value === billingId);
        const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;

        if (selectedBilling) {
            const billingMatch = addressObjects.find(
                (addr) =>
                    (addr.address_details ? addr.address_details._id : addr._id) === selectedBilling.value
            );

            if (isOffice) {
                if (!selectedOfficeBillingAddress || selectedOfficeBillingAddress._id !== billingMatch?._id) {
                    console.log("Auto-setting Office Billing Address:", billingMatch);
                    setSelectedOfficeBillingAddress(billingMatch || null);
                }
            } else {
                const agentBillingDetails = billingMatch?.address_details || null;
                if (!selectedBillingAddress || selectedBillingAddress._id !== agentBillingDetails?._id) {
                    console.log("Auto-setting Agent Billing Address:", agentBillingDetails);
                    setSelectedBillingAddress(agentBillingDetails);
                }
            }
        }
    }, [
        installationOrder,
        formik.values.billingAddressId,
        addressOptions,
        selectedOfficeAgent,
        selectedAgent,
        selectedOffice,
    ]);

    const completeOrder = async (getmethod: string) => {
        console.log("getmethod", getmethod);
        setLoading(true);

        try {
            if (["cheque", "bank_transfer"].includes(getmethod)) {
                // 🔁 Call Net30-style API
                const latestPayment = installationOrder?.payment_info?.[0]?.amount_due;
                console.log("latestPayment", latestPayment);
                const lastAmountDue = latestPayment ?? formik.values.total;
                console.log("lastAmountDue", lastAmountDue);
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/${installationOrder?._id}/net30_payment`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`, // use token from context
                        },
                        body: JSON.stringify({
                            payment_id: formik.values.transactionId || "",
                            method: getmethod,
                            amount_paid: formik.values.partial_amount,
                            total_amount: lastAmountDue,
                            amount_due: Math.max(0, lastAmountDue - formik.values.partial_amount),
                            payment_date: new Date().toISOString(),
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error("Cheque/Bank payment failed.");
                }

            } else {
                // 🔁 Default online payment API
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/${installationOrder?._id}/payment`,
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            payment_id: "",
                            total_amount: formik.values.total,
                            amount_paid: 0,
                            amount_due: formik.values.total,
                            payment_date: new Date().toISOString(),
                            method: getmethod,
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error("Online payment failed.");
                }
            }

            toast.success("Payment submitted successfully!");
            navigate("/teammember/jobs/installations");
            localStorage.removeItem("cart_id");

        } catch (error) {
            const errMessage =
                (error as Error).message || "An unknown error occurred";
            console.error("Order Failed:", errMessage);
            toast.error(`Error: ${errMessage}`);
        } finally {
            setLoading(false);
        }
    };


    const handleInstallationStatus = async (nextStatus: number) => {
        if (!installationId || !token) return;

        setLoading(true);

        try {
            let url = "";
            let body: any = undefined;

            const scheduledDate = installationOrder?.installation_details?.scheduled_date;

            switch (nextStatus) {
                case 200: // Finalize
                    url = `${process.env.REACT_APP_API_URL}/installation_orders/${installationId}/finalize`;
                    break;

                case 600:
                    url = `${process.env.REACT_APP_API_URL}/installation_orders/${installationId}/cancel`;
                    body = { note: formik.values.instructions };
                    break;

                default:
                    toast.error("Invalid status selected");
                    setLoading(false);
                    return;
            }

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                ...(body ? { body: JSON.stringify(body) } : {}), // only include body if defined
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result?.error || "Status update failed");

            toast.success(result.message || "Installation status updated");
            setInstallationOrder((prev: any) => ({ ...prev, status: nextStatus }));
        } catch (err: any) {
            console.error("Status update error:", err.message);
            toast.error(err.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const totalPaid = (installationOrder?.payment_info ?? []).reduce(
        (acc: number, payment: { amount_paid?: number | string }) =>
            acc + Number(payment.amount_paid || 0),
        0
    );

    const maxAllowed = (installationOrder?.payment_info ?? []).length > 0 ? Number((Number(installationOrder?.total_amount || 0) - totalPaid).toFixed(2)) : Number(installationOrder?.total_amount || 0);


    const remaingamount = ((installationOrder?.total_amount || 0) - totalPaid)

    const statusHistory: any[] = Array.isArray(installationOrder?.status_history)
        ? (installationOrder?.status_history as any[])
        : [];
    // console.log("statusHistory", statusHistory);
    const paymentInfo: PaymentInfoItem[] = [...(installationOrder?.payment_info || [])];

    const resolveOrderStatus = (status: number | undefined): string => {
        switch (status) {
            case null: return "";
            case 100: return "Draft";
            case 200: return "Finalized";
            case 300: return "Scheduled";
            case 400: return "In Progress";
            case 500: return "Completed";
            case 600: return "Canceled";
            default: return status !== undefined ? `Status ${status}` : "Unknown";
        }
    };


    const parseDate = (input: any): string => {
        try {
            return new Date(input).toLocaleString();
        } catch {
            return "Invalid Date";
        }
    };

    useEffect(() => {
        if (installationOrder?.uploaded_files) {
            setUploadedFileMeta(installationOrder.uploaded_files);
        }
    }, [installationOrder]);

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div className="content">
            <div className="card">
                <div className="card-body">
                    <h2>Installation Request - Edit</h2>
                    <div className="row mb-3">
                        <div className="col-md-12 text-end">
                            {installationOrder?.status === 100 && (
                                <button className="btn btn-primary" onClick={() => handleInstallationStatus(200)}>
                                    Click to Finalize
                                </button>
                            )}
                            {(installationOrder?.status <= 100) && (
                                <button className="btn btn-danger ms-3" onClick={() => handleInstallationStatus(600)}>
                                    Cancel Order
                                </button>
                            )}
                        </div>

                    </div>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label htmlFor="userType" className="form-label">
                                    Buyer Type
                                </label>
                                <Select
                                    className="form-control"
                                    id="userType"
                                    name="userType"
                                    options={buyerOptions}
                                    value={buyerOptions?.find(
                                        (option) =>
                                            option.value === formik.values.userType
                                    )}
                                    onChange={(selectedOption) => {
                                        formik.setFieldValue("userType", selectedOption?.value || "");
                                        if (selectedOption?.value === "office") {
                                            fetchOffices();
                                        }
                                    }}
                                    onBlur={formik.handleBlur}
                                    isDisabled
                                />
                            </div>
                            {
                                formik.values.userType === "office" ? (
                                    <>
                                        {/* <div className="col-md-4 mb-3">
                                            <label htmlFor="officeSelection">
                                                Select Team Member
                                            </label>
                                            <Select
                                                id="teamMemberId"
                                                name="teamMemberId"
                                                className="form-control"
                                                options={teamMemberOptions}
                                                value={teamMemberOptions?.find((option) => option.value === formik.values.teamMemberId) || null}
                                                onChange={(selected) => {
                                                    formik.setFieldValue("teamMemberId", selected?.value || "");
                                                    setSelectedTeamMember(selected);
                                                    if (selected?.value) {
                                                        fetchSuppliersByUserId(selected.value);
                                                    }
                                                }}
                                                placeholder="Select a team member"
                                                isDisabled
                                            />
                                        </div> */}

                                        <div className="col-md-4 mb-3">
                                            <label htmlFor="officeSelection">
                                                Select Office
                                            </label>
                                            <Select
                                                className="form-control"
                                                id="officeSelection"
                                                options={officeOptions}
                                                value={
                                                    officeOptions?.find(
                                                        (option) =>
                                                            option.value === formik.values.office._id
                                                    ) || null
                                                }
                                                onChange={(selected) => {
                                                    fetchOfficeAgents(selected);
                                                    if (selected?.value) {
                                                        fetchSuppliersByUserId(selected.value);
                                                    }
                                                    // console.log("Selected Office:", selected);
                                                    formik.setFieldValue(
                                                        "userFirstName",
                                                        selected?.firstName || ""
                                                    );
                                                    formik.setFieldValue(
                                                        "userLastName",
                                                        selected?.lastName || ""
                                                    );
                                                    formik.setFieldValue(
                                                        "userEmail",
                                                        selected?.email || ""
                                                    );
                                                    formik.setFieldValue(
                                                        "userPhone",
                                                        selected?.phone || ""
                                                    );
                                                }}
                                                isDisabled
                                            />
                                        </div>
                                        {officeAgentOptions && (
                                            <div className="col-md-4 mb-3">
                                                <label htmlFor="officeSelection">
                                                    Select Agent
                                                </label>
                                                <Select
                                                    className="form-control"
                                                    id="officeAgentSelection"
                                                    options={officeAgentOptions}
                                                    value={selectedOfficeAgent}
                                                    onChange={(selected) => {
                                                        setSelectedOfficeAgent(selected);
                                                        setSelectedAgent(selected);
                                                        formik.setFieldValue(
                                                            "userFirstName",
                                                            selected?.firstName || ""
                                                        );
                                                        formik.setFieldValue(
                                                            "userLastName",
                                                            selected?.lastName || ""
                                                        );
                                                        formik.setFieldValue(
                                                            "userEmail",
                                                            selected?.email || ""
                                                        );
                                                        formik.setFieldValue(
                                                            "userPhone",
                                                            selected?.phone || ""
                                                        );

                                                        if (selected?.value) {
                                                            fetchSuppliersByUserId(selected.value);
                                                        }

                                                    }}
                                                    isDisabled
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    formik.values.userType === "agent" && (
                                        <div className="col-md-4 mb-3">
                                            <label htmlFor="agentSelection">
                                                Select Agent
                                            </label>
                                            <Select
                                                className="form-control"
                                                id="agentSelection"
                                                options={agentOptions}
                                                value={selectedAgent}
                                                onChange={(selected) => {
                                                    setSelectedAgent(selected);
                                                    formik.setFieldValue(
                                                        "userFirstName",
                                                        selected?.firstName || ""
                                                    );
                                                    formik.setFieldValue(
                                                        "userLastName",
                                                        selected?.lastName || ""
                                                    );
                                                    formik.setFieldValue(
                                                        "userEmail",
                                                        selected?.email || ""
                                                    );
                                                    formik.setFieldValue(
                                                        "userPhone",
                                                        selected?.phone || ""
                                                    );

                                                    if (selected?.value) {
                                                        fetchSuppliersByUserId(selected.value);
                                                    }
                                                }}
                                                isDisabled
                                            />
                                        </div>
                                    )
                                )
                            }

                            <div className="col-md-4 mb-3">
                                <label htmlFor="installer" className="form-label">
                                    Select Installer <span className="text-danger">*</span>
                                </label>
                                <Select
                                    className="form-control"
                                    id="installer"
                                    name="installer"
                                    options={installerOptions}
                                    value={installerOptions.find((option) => option.value === formik.values.installer)}
                                    onChange={(selected) => {
                                        formik.setFieldValue("installer", selected?.value || "");
                                        setSelectedInstaller(selected);

                                        const supplierId = selected?.value ?? "";

                                        if (selectedOfficeAgent?.value) {
                                            fetchLinkedProducts("agent", selectedOfficeAgent.value, supplierId);
                                        } else if (selectedAgent?.value) {
                                            fetchLinkedProducts("agent", selectedAgent.value, supplierId);
                                        } else if (selectedOffice?.value) {
                                            fetchLinkedProducts("office", selectedOffice.value, supplierId);
                                        }
                                    }}
                                    onBlur={formik.handleBlur}
                                    isDisabled
                                />

                                {/* {formik.touched.installer && formik.errors.installer && (
                                        <div className="text-danger">{formik.errors.installer}</div>
                                    )} */}
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label">
                                    Select Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className={`form-control ${formik.touched.scheduled_date &&
                                        formik.errors.scheduled_date
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                    id="scheduled_date"
                                    name="scheduled_date"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.scheduled_date}
                                    disabled={installationOrder?.status > 100}
                                />
                                {/* {formik.touched.scheduled_date &&
                                        formik.errors.scheduled_date && (
                                            <div className="invalid-feedback">
                                                {formik.errors.scheduled_date}
                                            </div>
                                        )} */}
                            </div>
                            <div className="col-md-2 mb-3">
                                <label className="form-label">
                                    Priority
                                </label>
                                <Select
                                    id="priority"
                                    name="priority"
                                    options={priorityOptions}
                                    value={priorityOptions.find((option) => option.value === formik.values.priority) || null}
                                    onChange={(option) =>
                                        formik.setFieldValue("priority", option?.value || "")
                                    }
                                    onBlur={formik.handleBlur}
                                    isDisabled={installationOrder?.status > 100}
                                />
                                {/* {formik.touched.priority && formik.errors.priority && (
                                        <div className="text-danger">{formik.errors.priority}</div>
                                    )} */}
                            </div>
                        </div>

                        <div className="row">
                            <h5>Address Details </h5>
                            <div className="my-2">
                                <input
                                    type="checkbox"
                                    id="isSameAsBilling"
                                    name="isSameAsBilling"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    checked={formik.values.isSameAsBilling}
                                />{" "}
                                Billing and Installation Address are Different

                            </div>
                            <div className="row mb-4">
                                <div className="col-md-5">
                                    <div className="mb-3">
                                        <label
                                            htmlFor="billingAddressId"
                                            className="form-label"
                                        >
                                            Billing Address
                                        </label>
                                        <Select
                                            className="form-control"
                                            id="billingAddress"
                                            name="billingAddress"
                                            options={addressOptions}
                                            value={
                                                addressOptions?.find(
                                                    (option) =>
                                                        option.value === formik.values.billingAddressId
                                                ) || null
                                            }
                                            onChange={(selected: any) => {
                                                console.log("Selected Billing Address:", selected); // Debugging log
                                                formik.setFieldValue(
                                                    "billingAddressId",
                                                    selected?._id
                                                ); // Ensure it's set
                                                const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
                                                console.log("isOffice :", isOffice)

                                                if (isOffice) {
                                                    // selected is OfficeAddress
                                                    const selectedAddress = addressObjects?.find(
                                                        (addr) => addr._id === selected._id
                                                    );
                                                    console.log("OfficeBillingAddress In Select:", selectedAddress);
                                                    setSelectedOfficeBillingAddress(selectedAddress || null);
                                                } else {
                                                    const agentBillingAddress = addressObjects?.find(
                                                        (addr) => addr._id === selected._id
                                                    );
                                                    console.log("agentBillingAddress :", agentBillingAddress.address_details)
                                                    setSelectedBillingAddress(agentBillingAddress.address_details || null);
                                                }
                                            }}
                                            isDisabled={installationOrder?.status > 100}
                                        />
                                    </div>
                                </div>

                                {formik.values.isSameAsBilling === true ? null : (
                                    <div className="col-md-5">
                                        <div className="mb-3">
                                            <label
                                                htmlFor="installationAddressId"
                                                className="form-label"
                                            >
                                                Installation Address
                                            </label>
                                            {isLoaded && (
                                                <Autocomplete
                                                    onLoad={(autocomplete) => {
                                                        autocompleteRef.current = autocomplete;
                                                    }}
                                                    onPlaceChanged={handlePlaceChanged}
                                                >
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="installationAddressId"
                                                        name="installationAddressId"
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        value={formik.values.installationAddressId}
                                                        disabled={installationOrder?.status > 100}
                                                    />
                                                </Autocomplete>
                                            )}
                                            {/* <Select
                          className="form-control"
                          id="installationAddressId"
                          name="installationAddressId"
                          options={addressOptions}
                          value={addressObjects?.find(
                            (address) =>
                              address._id === formik.values.installationAddressId
                          )}
                          onChange={(selected: any) => {
                            formik.setFieldValue(
                              "installationAddressId",
                              selected?._id
                            )
                            const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
                            console.log("isOffice :", isOffice)
    
                            if (isOffice) {
                              // selected is OfficeAddress
                              const selectedAddress = addressObjects?.find(
                                (addr) => addr._id === selected._id
                              );
                              console.log("OfficeBillingAddress In Select:", selectedAddress);
                              setSelectedOfficeBillingAddress(selectedAddress || null);
                            } else {
                              const agentBillingAddress = addressObjects?.find(
                                (addr) => addr._id === selected._id
                              );
                              console.log("agentBillingAddress :", agentBillingAddress.address_details)
                              setSelectedBillingAddress(agentBillingAddress.address_details || null);
                            }
    
                          }
                          }
                        /> */}
                                        </div>
                                    </div>
                                )}
                                {installationOrder?.status === 100 &&
                                    <div className="col-md-2">
                                        <p>&nbsp;</p>
                                        <i
                                            className="ri-search-line fs-3 text-bg-info"
                                            style={{ cursor: "pointer" }}
                                            title={`${isMarkerDraggable ? "Disable Marker Drag" : "Enable Marker Drag"}`}
                                            onClick={() => {
                                                const newValue = !isMarkerDraggable;
                                                setIsMarkerDraggable(newValue);
                                                toast.info(
                                                    newValue
                                                        ? "You can now move the marker on the map."
                                                        : "Marker dragging disabled."
                                                );
                                            }}

                                        ></i>
                                    </div>
                                }
                            </div>

                            <div className="row mb-3">
                                <h5>Map View</h5>
                                <div style={mapContainerStyle}>
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        zoom={10}
                                        center={selectedLocation || center}
                                        // onClick={handleMapClick}
                                    >
                                        {selectedLocation && <Marker
                                            position={selectedLocation}
                                            draggable={isMarkerDraggable}
                                            onDragEnd={handleMarkerDragEnd}
                                        />
                                        }
                                    </GoogleMap>
                                </div>
                                {formik.touched.location && formik.errors.location && (
                                    <div className="text-danger">{formik.errors.location}</div>
                                )}
                            </div>

                            <div className="col-md-12">
                                <h4>Product Selection</h4>
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <Select
                                            id="products"
                                            name="products"
                                            options={productOptions}
                                            value={selectedProductOption}
                                            onChange={(selected) => {
                                                setSelectedProductOption(selected)
                                            }}
                                            onBlur={formik.handleBlur}
                                            isDisabled={installationOrder?.status > 100}
                                        />

                                        {formik.touched.products && formik.errors.products && (
                                            <div className="text-danger">{formik.errors.products}</div>
                                        )}
                                    </div>
                                    <div className="col-md-3">
                                        <button
                                            className="btn btn-success"
                                            type="button"
                                            onClick={() => {
                                                if (!selectedProductOption) return;

                                                const alreadyExists = selectedProducts.some(
                                                    (prod) => prod.product_id === selectedProductOption.value
                                                );
                                                if (alreadyExists) {
                                                    toast.warning("Product already added");
                                                    return;
                                                }

                                                const fullProduct = productObjs.find(
                                                    (p) => p.product_id === selectedProductOption.value
                                                );

                                                if (fullProduct) {
                                                    const flattenedProduct = {
                                                        product_id: fullProduct.product_id,
                                                        name: fullProduct.product.name,
                                                        sku: fullProduct.product.sku || "",
                                                        inventory_type: fullProduct.inventory_type,
                                                        product_location_status: fullProduct.product.product_location_status,
                                                        user_id: fullProduct.agent_id || fullProduct.office_id,
                                                        quantity: 1,
                                                        updateStatus: "",
                                                        productionStatus: "",
                                                    };

                                                    setSelectedProducts((prev) => [...prev, flattenedProduct]);
                                                    setSelectedProductOption(null);
                                                }
                                                console.log("selectedProducts", selectedProducts);
                                            }}
                                            disabled={installationOrder?.status > 100}
                                        >
                                            Add Product
                                        </button>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-12">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th className="text-center">Name</th>
                                                    <th className="text-center">SKU</th>
                                                    <th className="text-center">Qty </th>
                                                    <th className="text-center">Source </th>
                                                    <th className="text-center">Product Location Status</th>
                                                    <th className="text-center">Production Status </th>
                                                    <th className="text-center">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    selectedProducts?.filter((p) => p.inventory_type !== "rotational" && p.inventory_type !== "").map((item, index) => {
                                                        // const allocated = allocatedUnits[item.product_id] || [];
                                                        // const remaining = item.quantity - allocated.length;
                                                        return (
                                                            <tr key={item.product_id}>
                                                                <td className="text-center">{item.name}</td>
                                                                <td className="text-center">{item.sku}</td>
                                                                <td className="text-center">
                                                                    <input
                                                                        type="number"
                                                                        className="form-control text-center"
                                                                        value={item.quantity}
                                                                        min={1}
                                                                        onChange={(e) => {
                                                                            const qty = Number(e.target.value);
                                                                            const updated = [...selectedProducts];
                                                                            updated[index].quantity = qty;
                                                                            setSelectedProducts(updated);
                                                                        }}
                                                                    />

                                                                </td>
                                                                <td className="text-center">
                                                                    {item.inventory_type === "agent" ? "Agent Inventory" : "Office Inventory"}
                                                                </td>

                                                                <td className="text-center">
                                                                    <div className="d-flex justify-content-evenly">
                                                                        <span className="bg-warning px-3 py-2" style={{ borderRadius: "10px" }}>
                                                                            {item.product_location_status?.replace(/_/g, " ") || "N/A"}
                                                                        </span>
                                                                        {/* <button className="btn btn small btn-primary" onClick={() =>
                                                          setOpenStatusIndex(openStatusIndex === index ? null : index)
                                                        }>Update Status</button> */}
                                                                        <Link
                                                                            className="btn btn-small btn-primary"
                                                                            to={
                                                                                item.inventory_type === "agent"
                                                                                    ? "/teammember/agentInventory"
                                                                                    : "/teammember/officeInventory"
                                                                            }
                                                                        >
                                                                            Update Status
                                                                        </Link>

                                                                    </div>
                                                                    {/* {openStatusIndex === index && (
                                                        <>
                                                          <div className="mt-2">
                                                            <Select
                                                              id="update_status"
                                                              name="update_status"
                                                              options={[
                                                                { value: "with supplier", label: "With Supplier" },
                                                                { value: "in transit", label: "In Transit" },
                                                                { value: "on site", label: "On Site" },
                                                                { value: "declined", label: "Declined" },
                                                              ]}
                                                              onChange={(option) =>
                                                                formik.setFieldValue("update_status", option?.value || "")
                                                              }
                                                              onBlur={formik.handleBlur}
                                                            />
                                                          </div>
                                                          <div className="mt-2">
                                                            <button className="btn btn-info small w-100">Update</button>
                                                          </div>
                                                        </>
                                                      )} */}
                                                                </td>
                                                                <td className="text-center">
                                                                    {/* <Select
                                                        id="product_status"
                                                        name="product_status"
                                                        options={[
                                                          { value: "not started", label: "Not Started" },
                                                          { value: "in production", label: "In Production" },
                                                          { value: "quality check", label: "Quality Check" },
                                                          { value: "ready for installation", label: "Ready for Installation" },
                                                        ]}
                                                        onChange={(option) =>
                                                          formik.setFieldValue("company", option?.value || "")
                                                        }
                                                        onBlur={formik.handleBlur}
                                                      /> */}
                                                                    ---
                                                                </td>
                                                                <td className="text-center d-flex gap-2 justify-content-center">
                                                                    {/* <button
                                        type="button"
                                        className="btn btn-success btn-sm"
                                        disabled={remaining <= 0}
                                        onClick={() => {
                                          setLinkedProductIdContext(item.product_id);
                                          setSelectedProduct(null);
                                          setSelectedWarehouse(null);
                                          setAvailableSerializedUnits([]);
                                          fetchRotationalPoolProducts();
                                          setShowAllocateModal(true);
                                        }}
                                      >
                                        Allocate
                                      </button> */}

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger btn-sm"
                                                                        onClick={() => {
                                                                            setSelectedProducts((prev) =>
                                                                                prev.filter((prod) => prod.product_id !== item.product_id)
                                                                            );
                                                                            // setAllocatedUnits((prev) => {
                                                                            //   const updated = { ...prev };
                                                                            //   delete updated[item.product_id];
                                                                            //   return updated;
                                                                            // });
                                                                        }}
                                                                        disabled={installationOrder?.status > 100}
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })

                                                }

                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-md-12 my-3 text-end">

                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-7">
                                    <label htmlFor="">Instructions </label>
                                    <textarea
                                        name="instructions"
                                        id="instructions"
                                        className="form-control"
                                        placeholder="Add instructions here"
                                        value={formik.values.instructions}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        rows={4}
                                        disabled={installationOrder?.status > 100}
                                    ></textarea>

                                    {/* <div className="col-md-12 mt-3">
                                        <label htmlFor="">Proof of Installation</label>
                                        <input type="file" className="form-control" id="image" name="image" multiple onChange={(e) => {
                                            const files = Array.from(e.target.files || []);
                                            const newPreviewFiles = files.map((file) => ({
                                                file,
                                                url: URL.createObjectURL(file),
                                            }));
                                            setPreviewFiles((prev) => [...prev, ...newPreviewFiles]);
                                        }}
                                            disabled={installationOrder?.status > 100}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-success mt-3"
                                            onClick={async () => {
                                                try {
                                                    const uploads = await Promise.all(
                                                        previewFiles.map(async ({ file }) => {
                                                            const path = await uploadFile(file);
                                                            return {
                                                                file_name: file.name,
                                                                file_url: path,
                                                                content_type: file.type,
                                                                uploaded_date: new Date().toISOString(),
                                                            };
                                                        })
                                                    );

                                                    const allFiles = [...uploadedFileMeta, ...uploads];
                                                    const uniqueByName = allFiles.filter(
                                                        (file, index, self) =>
                                                            index === self.findIndex((f) => f.file_name === file.file_name)
                                                    );

                                                    setUploadedFileMeta(uniqueByName);
                                                    setPreviewFiles([]); // clear previews after upload
                                                    toast.success("Files uploaded successfully!");
                                                } catch (err) {
                                                    console.error("Upload error:", err);
                                                    toast.error("Failed to upload files.");
                                                }
                                            }}
                                            disabled={installationOrder?.status > 100}
                                        >
                                            Upload
                                        </button>

                                    </div> */}
                                </div>

                                <div className="col-md-3 offset-2">
                                    <table className="calculation-table mt-3">
                                        <tr>
                                            <th>Total Units</th>
                                            <td>{totalUnits}</td>
                                        </tr>
                                        <tr>
                                            <th>Installation Charge</th>
                                            <td>$ {Number(installationCost)?.toFixed(2) ?? "0.00"}</td>
                                        </tr>
                                        <tr>
                                            <th>Tax</th>
                                            <td>$ {Number(formik.values.tax).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <th>Subtotal</th>
                                            <td>$ {Number(formik.values.subtotal).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <th>Total</th>
                                            <td>$ {Number(formik.values.total).toFixed(2)}</td>
                                        </tr>
                                        {(installationOrder?.payment_info ?? []).length > 0 && (
                                            <>
                                                <tr>
                                                    <th>Already Paid:</th>
                                                    <th className="text-end">${totalPaid.toFixed(2)}</th>
                                                </tr>
                                                <tr>
                                                    <th>Remaining Balance:</th>
                                                    <th className="text-end">
                                                        <strong>${remaingamount.toFixed(2)}</strong>
                                                    </th>
                                                </tr>
                                            </>
                                        )}

                                        {formik.values.enable_partial_payment && (
                                            <tr>
                                                <th>Partial Payment:</th>
                                                <th className="text-end">
                                                    <strong>${Number(formik.values.partial_amount || 0).toFixed(2)}</strong>
                                                </th>
                                            </tr>
                                        )}
                                    </table>

                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            {uploadedFileMeta.map((file, index) => (
                                <div key={index} className="col-md-3 position-relative mb-3">
                                    {
                                        installationOrder?.status === 100 &&
                                        <div
                                            className="position-absolute top-0 end-0 bg-danger text-white rounded-circle"
                                            style={{
                                                width: "20px",
                                                height: "20px",
                                                textAlign: "center",
                                                cursor: "pointer",
                                                zIndex: 2,
                                            }}
                                            onClick={() => {
                                                setUploadedFileMeta((prev) => prev.filter((_, i) => i !== index));
                                            }}
                                        >
                                            &times;
                                        </div>
                                    }
                                    <div className="border p-2">
                                        <img
                                            src={`${process.env.REACT_APP_API_URL}/${file.file_url.replace(/\\/g, "/")}`}
                                            alt={file.file_name}
                                            className="img-fluid"
                                            style={{ height: "150px", objectFit: "cover", width: "100%" }}
                                        />
                                        <div className="small text-center mt-1 text-muted">{file.file_name}</div>
                                    </div>
                                </div>
                            ))}
                        </div>



                        {/* <div className="row justify-content-end">
                                <div className="col-md-3 mt-3">
                                    <label className="me-1 mb-1">Local Laws Compliance :</label><br />
                                    <input
                                        type="checkbox"
                                        id="localLaws"
                                        name="localLawsChecked"
                                        checked={formik.values.localLawsChecked}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <span>{" "}I acknowledge adherence to local laws</span>
    
                                </div>
                            </div> */}
                        {
                            installationOrder?.status === 100 &&
                            <button
                                type="submit"
                                className="btn btn-info mt-4 float-end"
                                disabled={formSubmitting}
                            >
                                {formSubmitting ? "Submitting..." : "Submit Request"}
                            </button>
                        }
                    </form>

                    {/* <div className="row mt-3">
                        <div className="col-md-9">
                            <h3>Payment Methods</h3>
                            {
                                installationOrder?.status === 100 && <h5 className="text-danger">To Get the Payment Method Finalize the Installation Order</h5>
                            }
                            {((installationOrder?.status ?? 0) > 100) && installationOrder?.payment_status !== "Paid" && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-success material-shadow-none btn-sm mt-2 mt-sm-0 me-1"
                                        onClick={() => setPayment((prev) => !prev)}
                                    >
                                        <i className="mdi mdi-cash-multiple align-middle me-1"></i>
                                        Process Payment
                                    </button>
                                </>
                            )}
                            {payment && installationOrder?._id && (
                                <div className="card-body">
                                    <div className="row my-3">
                                        <div className="col-md-6">
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="enable_partial_payment"
                                                    name="enable_partial_payment"
                                                    onChange={(e) => {
                                                        formik.handleChange(e);
                                                        if (!e.target.checked) {
                                                            formik.setFieldValue("partial_amount", 0);
                                                        }
                                                    }}
                                                    onBlur={formik.handleBlur}
                                                    checked={formik.values.enable_partial_payment}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="enable_partial_payment"
                                                >
                                                    Enable Partial Payment
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            {formik.values.enable_partial_payment && (
                                                <>
                                                    <div className="mt-2">
                                                        <label className="form-label">
                                                            Amount to Collect Now :
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="partial_amount"
                                                            id="partial_amount"
                                                            onChange={(e) => {
                                                                const enteredValue = Number(e.target.value);
                                                                formik.setFieldValue(
                                                                    "partial_amount",
                                                                    enteredValue <= maxAllowed ? enteredValue : maxAllowed
                                                                );
                                                            }}
                                                            onBlur={formik.handleBlur}
                                                            value={formik.values.partial_amount}
                                                        />

                                                    </div>
                                                    <div className="mt-2">
                                                        <span>
                                                            Remaining Balance: $
                                                            {(Number(remaingamount) - Number(formik.values.partial_amount || 0)).toFixed(2)}

                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            <Elements stripe={stripePromise}>
                                                <div>
                                                    <h5 className="mb-1">Payment Selection</h5>
                                                    <p className="text-muted mb-2">
                                                        Please select payment method
                                                    </p>
                                                </div>

                                                <div className="row">
                                                    <div className="col-lg-3 mb-3 col-sm-6">
                                                        <div className="form-check card-radio">
                                                            <input
                                                                id="paymentMethodOnline"
                                                                name="paymentMethod"
                                                                type="radio"
                                                                className="form-check-input"
                                                                checked={selectedPaymentMethod === "online"}
                                                                onChange={() =>
                                                                    setSelectedPaymentMethod("online")
                                                                }
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor="paymentMethodOnline"
                                                            >
                                                                <i className="ri-bank-card-fill align-bottom"></i>
                                                                <span> Online Payment</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-3 mb-3 col-sm-6">
                                                        <div className="form-check card-radio">
                                                            <input
                                                                id="paymentMethodcheque"
                                                                name="paymentMethod"
                                                                type="radio"
                                                                className="form-check-input"
                                                                checked={selectedPaymentMethod === "cheque"}
                                                                onChange={() =>
                                                                    setSelectedPaymentMethod("cheque")
                                                                }
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor="paymentMethodcheque"
                                                            >
                                                                <i className="ri-bank-card-fill align-bottom"></i>
                                                                <span> Cheque</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-3 mb-3 col-sm-6">
                                                        <div className="form-check card-radio">
                                                            <input
                                                                id="paymentMethodbank_transfer"
                                                                name="paymentMethod"
                                                                type="radio"
                                                                className="form-check-input"
                                                                checked={selectedPaymentMethod === "bank_transfer"}
                                                                onChange={() =>
                                                                    setSelectedPaymentMethod("bank_transfer")
                                                                }
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor="paymentMethodbank_transfer"
                                                            >
                                                                <i className="ri-bank-card-fill align-bottom"></i>
                                                                <span> Bank Transfer</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                    {(formik.values.enable_partial_payment || installationOrder?.payment_info?.length) ? <></> :
                                                        (
                                                            paymentMethod?.map((method) => (
                                                                <div className="col-lg-3 mb-3 col-sm-6" key={method.id}>
                                                                    <div className="form-check card-radio">
                                                                        <input
                                                                            id={`paymentMethod_${method.id}`}
                                                                            name="paymentMethod"
                                                                            type="radio"
                                                                            className="form-check-input"
                                                                            checked={
                                                                                selectedPaymentMethod === method.name
                                                                            }
                                                                            onChange={() =>
                                                                                setSelectedPaymentMethod(method.name)
                                                                            }
                                                                        />
                                                                        <label
                                                                            className="form-check-label"
                                                                            htmlFor={`paymentMethod_${method.id}`}
                                                                        >
                                                                            <i className="ri-bank-card-fill align-bottom"></i>
                                                                            <span> {method.name}</span>
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )
                                                    }
                                                </div>

                                                {selectedPaymentMethod === "online" ? (
                                                    <div className="collapse show mt-4">
                                                        <div className="card p-4 border shadow-none">
                                                            <PaymentFormManualOrder
                                                                url="installation_orders"
                                                                method="POST"
                                                                supplierId={installationOrder?.signage_id}
                                                                totalAmount={remaingamount ? remaingamount : installationOrder?.total_amount}
                                                                paidAmount={
                                                                    formik.values.partial_amount === 0
                                                                        ? (Number(installationOrder?.total_amount || 0) - totalPaid)
                                                                        : formik.values.partial_amount
                                                                }
                                                                orderId={installationOrder?._id}
                                                                selectedPaymentMethod={selectedPaymentMethod}
                                                                onPaymentSuccess={(paymentIntentId) => {
                                                                    console.log(
                                                                        "Payment Success: ",
                                                                        paymentIntentId
                                                                    );
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    ["cheque", "bank_transfer"].includes(selectedPaymentMethod) ? (
                                                        <div className="mt-4">
                                                            <div className="card p-3 border shadow-none">
                                                                <h4 className="mb-3">
                                                                    {selectedPaymentMethod === "cheque" ? "Cheque Payment Details" : "Bank Transfer Details"}
                                                                </h4>
                                                                <div className="row">
                                                                    <div className="mb-3 col-md-6">
                                                                        <label htmlFor="transactionId" className="form-label">
                                                                            Transaction ID
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            id="transactionId"
                                                                            value={formik.values.transactionId} 
                                                                            onChange={(e) => formik.setFieldValue("transactionId", e.target.value)}
                                                                        />
                                                                    </div>

                                                                    <div className="mb-3 col-md-6">
                                                                        <label htmlFor="amount" className="form-label">
                                                                            Amount
                                                                        </label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            id="amount"
                                                                            value={formik.values.partial_amount}
                                                                            onChange={(e) =>
                                                                                formik.setFieldValue(
                                                                                    "partial_amount",
                                                                                    Number(e.target.value) <= maxAllowed ? Number(e.target.value) : maxAllowed
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary"
                                                                    onClick={() => {
                                                                        completeOrder(selectedPaymentMethod);
                                                                    }}
                                                                >
                                                                    Submit Payment
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        selectedPaymentMethod ? (
                                                            <div className="d-flex align-items-start gap-3 mt-4">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary"
                                                                    onClick={() => {
                                                                        completeOrder(selectedPaymentMethod);
                                                                    }}
                                                                >
                                                                    Complete Order
                                                                </button>
                                                            </div>
                                                        ) : null
                                                    )
                                                )}
                                            </Elements>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div> */}

                    <div className="card mt-4">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Installation Log</h5>
                        </div>
                        <div className="card-body">
                            <ul className="list-unstyled vstack gap-3">

                                {/* ✅ Payment Logs */}
                                {paymentInfo.slice().reverse().map((payment, idx) => {
                                    const status =
                                        payment.amount_due === 0
                                            ? "Paid"
                                            : payment.amount_paid > 0
                                                ? "Partial Payment"
                                                : "Pending";

                                    const badgeClass =
                                        status === "Paid"
                                            ? "bg-success-subtle text-success"
                                            : status === "Partial Payment"
                                                ? "bg-warning-subtle text-warning"
                                                : "bg-danger-subtle text-danger";

                                    return (
                                        <li key={`payment-${idx}`}>
                                            <div>
                                                <strong>💰 Payment:</strong>{" "}
                                                <span className="badge bg-success-subtle text-success">
                                                    ${payment.amount_paid?.toFixed(2)} {payment.method && `via ${payment.method}`}
                                                </span>

                                                <strong className="ms-3">💳 Payment Status:</strong>{" "}
                                                <span className={`badge ${badgeClass}`}>{status}</span>

                                                <div className="text-muted small">On {parseDate(payment.payment_date)}</div>
                                            </div>
                                        </li>
                                    );
                                })}

                                {/* ✅ Installation Status Logs */}
                                {statusHistory.map((log, idx) => (
                                    <li key={`status-${idx}`}>
                                        <div>
                                            <strong>📦 Installation Status:</strong>{" "}
                                            <span className="badge bg-secondary-subtle text-dark">
                                                {
                                                    log.from === null && resolveOrderStatus(log.from)
                                                }
                                                {resolveOrderStatus(log.from)} → {resolveOrderStatus(log.to)}
                                            </span>
                                            <div className="text-muted small">On {parseDate(log.timestamp)}</div>
                                            {log.note && <div className="small mt-1">📝 {log.reason}</div>}
                                        </div>
                                    </li>
                                ))}

                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrokerageTeammemberEditInstallation;
